import { useEffect, useRef } from 'react';
import logEvent from './logEvent';
import { LOG_EVENT_TYPE } from './eventTypes';

/**
 * useScreenViewLog
 * Fires SCREEN_VIEW once when a step component mounts. By default also
 * fires SCREEN_EXIT on unmount (covers moving forward, back-navigation, and
 * browser back) — pass { logExit: false } to skip that second log.
 *
 * Static/simple screens only need the one SCREEN_VIEW log — the next
 * screen's own SCREEN_VIEW already proves the host moved on, so a matching
 * SCREEN_EXIT is redundant there. The three complex screens (Connect PMS,
 * Coverage, Review Guidebook) keep the default (both), since knowing exactly
 * when the host left mid-process is meaningful for those.
 *
 * One line to add to any step file:
 *   useScreenViewLog('qualify');                    // SCREEN_VIEW + SCREEN_EXIT
 *   useScreenViewLog('qualify', { logExit: false }); // SCREEN_VIEW only
 *
 * This hook only observes component mount/unmount — it never reads or
 * writes any onboarding state, redux, or context, so it cannot change any
 * existing screen's behavior.
 */
const useScreenViewLog = (screen, { logExit = true } = {}) => {
  // Refs so the effect below can stay a mount/unmount-only effect (empty deps)
  // while still logging the latest values if they were ever to change.
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const logExitRef = useRef(logExit);
  logExitRef.current = logExit;

  useEffect(() => {
    logEvent(screenRef.current, LOG_EVENT_TYPE.SCREEN_VIEW);
    return () => {
      if (logExitRef.current) {
        logEvent(screenRef.current, LOG_EVENT_TYPE.SCREEN_EXIT);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useScreenViewLog;
